import React, { useState, useEffect, useCallback } from 'react';
import { Line, Pie, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import analyticsApi from '../../api/analyticsApi';
import { useToast } from '../../contexts/ToastContext';
import SkeletonLoader from '../../components/SkeletonLoader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
);

const Dashboard = () => {
  const { addToast } = useToast();
  const [loadingSummaryStats, setLoadingSummaryStats] = useState(true);
  const [loadingVisitorsByDate, setLoadingVisitorsByDate] = useState(true);
  const [loadingVisitorsByCountry, setLoadingVisitorsByCountry] = useState(true);
  const [loadingTrafficSources, setLoadingTrafficSources] = useState(true);
  const [loadingMostVisitedPages, setLoadingMostVisitedPages] = useState(true);
  const [loadingTopOperatingSystems, setLoadingTopOperatingSystems] = useState(true);
  const [loadingTopBrowsers, setLoadingTopBrowsers] = useState(true);

  const [summaryStats, setSummaryStats] = useState({
    totalVisitors: 0,
    newVisitors: 0,
    returningVisitors: 0,
    totalProjects: 0,
    newLeads: 0,
    totalSubscribers: 0, // Added totalSubscribers to initial state
  });
  const [visitorsByDate, setVisitorsByDate] = useState([]);
  const [visitorsByCountry, setVisitorsByCountry] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [mostVisitedPages, setMostVisitedPages] = useState([]);
  const [topOperatingSystems, setTopOperatingSystems] = useState([]);
  const [topBrowsers, setTopBrowsers] = useState([]);

  const [selectedDateRange, setSelectedDateRange] = useState('this_month'); // Default to 'This Month'

  // Helper function to return the selected date range key
  const getDateRangeParams = useCallback((rangeKey) => {
    return { dateRange: rangeKey };
  }, []);

  const fetchAllAnalyticsData = useCallback(async () => {
    const params = getDateRangeParams(selectedDateRange); // Pass the range key directly

    // Fetch Summary Stats
    setLoadingSummaryStats(true);
    try {
      const summaryRes = await analyticsApi.getSummaryStats(params);
      if (summaryRes.success) {
        setSummaryStats(summaryRes.data);
      } else {
        console.error('Failed to fetch summary stats:', summaryRes.message);
        addToast(summaryRes.message || 'Failed to load summary stats.', 'error');
      }
    } catch (err) {
      console.error('Error fetching summary stats:', err);
      addToast(err.response?.data?.message || 'Failed to load summary stats.', 'error');
    } finally {
      setLoadingSummaryStats(false);
    }

    // Fetch Visitors by Date
    setLoadingVisitorsByDate(true);
    try {
      const visitorsByDateRes = await analyticsApi.getVisitorsByDate(params);
      if (visitorsByDateRes.success) {
        const fetchedData = visitorsByDateRes.data;

        if (fetchedData.length > 0) {
          // Sort fetched data by date to easily find min/max
          fetchedData.sort((a, b) => new Date(a.date) - new Date(b.date));

          const minDate = new Date(fetchedData[0].date);
          const maxDate = new Date(fetchedData[fetchedData.length - 1].date);

          const dateMap = new Map(fetchedData.map(item => [item.date, item]));

          const fullDateRangeData = [];
          let currentDate = new Date(minDate);

          while (currentDate <= maxDate) {
            const dateString = currentDate.toISOString().slice(0, 10); // YYYY-MM-DD
            if (dateMap.has(dateString)) {
              fullDateRangeData.push(dateMap.get(dateString));
            } else {
              fullDateRangeData.push({ date: dateString, visitors: 0, pageViews: 0 });
            }
            currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
          }

          setVisitorsByDate(fullDateRangeData);
        } else {
          setVisitorsByDate([]); // No data, set to empty array
        }
      } else {
        console.error('Failed to fetch visitors by date:', visitorsByDateRes.message);
        addToast(visitorsByDateRes.message || 'Failed to load visitors by date.', 'error');
      }
    } catch (err) {
      console.error('Error fetching visitors by date:', err);
      addToast(err.response?.data?.message || 'Failed to load visitors by date.', 'error');
    } finally {
      setLoadingVisitorsByDate(false);
    }

    // Fetch Visitors by Country
    setLoadingVisitorsByCountry(true);
    try {
      const visitorsByCountryRes = await analyticsApi.getVisitorsByCountry(params);
      if (visitorsByCountryRes.success) {
        setVisitorsByCountry(visitorsByCountryRes.data);
      } else {
        console.error('Failed to fetch visitors by country:', visitorsByCountryRes.message);
        addToast(visitorsByCountryRes.message || 'Failed to load visitors by country.', 'error');
      }
    } catch (err) {
      console.error('Error fetching visitors by country:', err);
      addToast(err.response?.data?.message || 'Failed to load visitors by country.', 'error');
    } finally {
      setLoadingVisitorsByCountry(false);
    }

    // Fetch Traffic Sources
    setLoadingTrafficSources(true);
    try {
      const trafficSourcesRes = await analyticsApi.getTrafficSources(params);
      if (trafficSourcesRes.success) {
        setTrafficSources(trafficSourcesRes.data);
      } else {
        console.error('Failed to fetch traffic sources:', trafficSourcesRes.message);
        addToast(trafficSourcesRes.message || 'Failed to load traffic sources.', 'error');
      }
    } catch (err) {
      console.error('Error fetching traffic sources:', err);
      addToast(err.response?.data?.message || 'Failed to load traffic sources.', 'error');
    } finally {
      setLoadingTrafficSources(false);
    }

    // Fetch Most Visited Pages
    setLoadingMostVisitedPages(true);
    try {
      const mostVisitedPagesRes = await analyticsApi.getMostVisitedPages(params);
      if (mostVisitedPagesRes.success) {
        setMostVisitedPages(Object.values(mostVisitedPagesRes.data));
      } else {
        console.error('Failed to fetch most visited pages:', mostVisitedPagesRes.message);
        addToast(mostVisitedPagesRes.message || 'Failed to load most visited pages.', 'error');
      }
    } catch (err) {
      console.error('Error fetching most visited pages:', err);
      addToast(err.response?.data?.message || 'Failed to load most visited pages.', 'error');
    } finally {
      setLoadingMostVisitedPages(false);
    }

    // Fetch Top Operating Systems
    setLoadingTopOperatingSystems(true);
    try {
      const osRes = await analyticsApi.getTopOperatingSystems(params);
      if (osRes.success) {
        setTopOperatingSystems(osRes.data);
      } else {
        console.error('Failed to fetch top operating systems:', osRes.message);
        addToast(osRes.message || 'Failed to load top operating systems.', 'error');
      }
    } catch (err) {
      console.error('Error fetching top operating systems:', err);
      addToast(err.response?.data?.message || 'Failed to load top operating systems.', 'error');
    } finally {
      setLoadingTopOperatingSystems(false);
    }

    // Fetch Top Browsers
    setLoadingTopBrowsers(true);
    try {
      const browserRes = await analyticsApi.getTopBrowsers(params);
      if (browserRes.success) {
        setTopBrowsers(browserRes.data);
      } else {
        console.error('Failed to fetch top browsers:', browserRes.message);
        addToast(browserRes.message || 'Failed to load top browsers.', 'error');
      }
    } catch (err) {
      console.error('Error fetching top browsers:', err);
      addToast(err.response?.data?.message || 'Failed to load top browsers.', 'error');
    } finally {
      setLoadingTopBrowsers(false);
    }
  }, [addToast, getDateRangeParams, selectedDateRange]);

  useEffect(() => {
    fetchAllAnalyticsData();
  }, [fetchAllAnalyticsData]);

  const visitorsByDateChartData = {
    labels: visitorsByDate.map(item => item.date),
    datasets: [
      {
        label: 'Visitors',
        data: visitorsByDate.map(item => item.visitors),
        fill: false,
        borderColor: '#E11D48',
        tension: 0.1,
      },
      {
        label: 'Page Views',
        data: visitorsByDate.map(item => item.pageViews),
        fill: false,
        borderColor: '#60A5FA',
        tension: 0.1,
      },
    ],
  };

  const visitorsByDateOptions = {
    responsive: true,
    maintainAspectRatio: false, // Added for responsiveness
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#9CA3AF',
        }
      },
      title: {
        display: true,
        text: 'Visitors & Page Views by Date',
        color: '#FFFFFF',
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        }
      },
      y: {
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        }
      }
    }
  };

  const visitorsByCountryChartData = {
    labels: visitorsByCountry.map(item => item.country),
    datasets: [
      {
        data: visitorsByCountry.map(item => item.sessions),
        backgroundColor: [
          '#E11D48', '#60A5FA', '#34D399', '#FBBF24', '#A78BFA',
          '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6',
        ],
        borderColor: '#0B0F19',
        borderWidth: 1,
      },
    ],
  };

  const visitorsByCountryOptions = {
    responsive: true,
    maintainAspectRatio: false, // Added for responsiveness
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#9CA3AF',
        }
      },
      title: {
        display: true,
        text: 'Visitors by Country',
        color: '#FFFFFF',
      },
    },
  };

  const topOperatingSystemsChartData = {
    labels: topOperatingSystems.map(item => item.operatingSystem),
    datasets: [
      {
        data: topOperatingSystems.map(item => item.sessions),
        backgroundColor: [
          '#E11D48', '#60A5FA', '#34D399', '#FBBF24', '#A78BFA',
          '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6',
        ],
        borderColor: '#0B0F19',
        borderWidth: 1,
      },
    ],
  };

  const topOperatingSystemsOptions = {
    responsive: true,
    maintainAspectRatio: false, // Added for responsiveness
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: '#9CA3AF',
        }
      },
      title: {
        display: true,
        text: 'Top Operating Systems',
        color: '#FFFFFF',
      },
    },
  };

  const topBrowsersChartData = {
    labels: topBrowsers.map(item => item.browser),
    datasets: [
      {
        label: 'Sessions',
        data: topBrowsers.map(item => item.sessions),
        backgroundColor: [
          '#E11D48', '#60A5FA', '#34D399', '#FBBF24', '#A78BFA',
          '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6',
        ],
        borderColor: '#0B0F19',
        borderWidth: 1,
        barThickness: 20,
      },
    ],
  };

  const topBrowsersOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false, // Added for responsiveness
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#9CA3AF',
        }
      },
      title: {
        display: true,
        text: 'Top Browsers',
        color: '#FFFFFF',
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        }
      },
      y: {
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(255,255,255,0.1)',
        }
      }
    }
  };

  const summaryCardsData = [
    { id: 1, name: 'Total Visitors', value: summaryStats.totalVisitors },
    { id: 2, name: 'New Visitors', value: summaryStats.newVisitors },
    { id: 3, name: 'Returning Visitors', value: summaryStats.returningVisitors },
    { id: 4, name: 'Total Projects', value: summaryStats.totalProjects },
    { id: 5, name: 'New Leads', value: summaryStats.newLeads },
    { id: 6, name: 'Subscribers', value: summaryStats.totalSubscribers },
  ];

  const dateRanges = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'this_week', label: 'This Week' },
    { key: 'last_week', label: 'Last Week' },
    { key: 'this_month', label: 'This Month' },
    { key: 'last_month', label: 'Last Month' },
    { key: 'last_6_months', label: 'Last 6 Months' },
  ];

  return (
    <div className="p-6 bg-bg-dark text-text-white font-sans min-h-screen">
      <div className="flex justify-end mb-4 overflow-x-auto whitespace-nowrap"> {/* Added overflow-x-auto and whitespace-nowrap */}
        {dateRanges.map((range) => (
          <button
            key={range.key}
            onClick={() => setSelectedDateRange(range.key)}
            className={`px-4 py-2 mx-1 rounded-md text-sm font-medium ${
              selectedDateRange === range.key
                ? 'bg-accent-red text-white'
                : 'bg-gray-700 text-text-gray hover:bg-gray-600'
            }`}
          >
            {range.label}
          </button>
        ))}
      </div>

      {loadingSummaryStats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <SkeletonLoader type="dashboard" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
          {summaryCardsData.map(card => (
            <div key={card.id} className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle text-center min-h-[120px] flex flex-col justify-center"> {/* Added min-h and flex for centering */}
              <div className="text-4xl font-bold text-accent-red mb-2">{card.value}</div>
              <div className="text-lg text-text-gray">{card.name}</div>
            </div>
          ))}
        </div>
      )}

      {loadingVisitorsByDate ? (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle mb-8 relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
          <SkeletonLoader type="chart" />
        </div>
      ) : visitorsByDate.length > 0 ? (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle mb-8 relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
          <Line data={visitorsByDateChartData} options={visitorsByDateOptions} />
        </div>
      ) : (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle mb-8 text-center text-text-gray min-h-[300px] flex items-center justify-center"> {/* Added min-h and flex for centering */}
          No visitor data by date available.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {loadingVisitorsByCountry ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
            <SkeletonLoader type="chart" />
          </div>
        ) : visitorsByCountry.length > 0 ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
            <Pie data={visitorsByCountryChartData} options={visitorsByCountryOptions} />
          </div>
        ) : (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle text-center text-text-gray min-h-[300px] flex items-center justify-center"> {/* Added min-h and flex for centering */}
            No visitor data by country available.
          </div>
        )}

        {loadingTrafficSources ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle min-h-[300px]"> {/* Added min-h */}
            <SkeletonLoader type="table" />
          </div>
        ) : trafficSources.length > 0 ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle min-h-[300px] flex flex-col"> {/* Added min-h and flex-col */}
            <h2 className="text-xl font-bold text-text-white mb-4">Traffic Source</h2>
            <div className="overflow-x-auto overflow-y-auto flex-grow"> {/* Added overflow-y-auto and flex-grow */}
              <table className="min-w-full bg-gray-800 border border-gray-700">
                <thead>
                  <tr>
                    <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Source & Medium</th>
                    <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {trafficSources.map((source, index) => (
                    <tr key={index} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{source.source} / {source.medium}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{source.sessions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle text-center text-text-gray min-h-[300px] flex items-center justify-center"> {/* Added min-h and flex for centering */}
            No traffic source data available.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {loadingTopOperatingSystems ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
            <SkeletonLoader type="chart" />
          </div>
        ) : topOperatingSystems.length > 0 ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
            <Doughnut data={topOperatingSystemsChartData} options={topOperatingSystemsOptions} />
          </div>
        ) : (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle text-center text-text-gray min-h-[300px] flex items-center justify-center"> {/* Added min-h and flex for centering */}
            No operating system data available.
          </div>
        )}

        {loadingTopBrowsers ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
            <SkeletonLoader type="chart" />
          </div>
        ) : topBrowsers.length > 0 ? (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle relative min-h-[300px]"> {/* Changed h-64 to min-h-[300px] */}
            <Bar data={topBrowsersChartData} options={topBrowsersOptions} />
          </div>
        ) : (
          <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle text-center text-text-gray min-h-[300px] flex items-center justify-center"> {/* Added min-h and flex for centering */}
            No browser data available.
          </div>
        )}
      </div>

      {loadingMostVisitedPages ? (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle min-h-[300px]"> {/* Added min-h */}
          <SkeletonLoader type="table" />
        </div>
      ) : mostVisitedPages.length > 0 ? (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle min-h-[300px] flex flex-col"> {/* Added min-h and flex-col */}
          <h2 className="text-xl font-bold text-text-white mb-4">Most Visited Pages</h2>
          <div className="overflow-x-auto overflow-y-auto flex-grow"> {/* Added overflow-y-auto and flex-grow */}
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Page Path</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Page Title</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Visits</th>
                </tr>
              </thead>
              <tbody>
                {mostVisitedPages.map((page, index) => (
                  <tr key={index} className="hover:bg-gray-700">
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{page.url}</td>
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{page.pageTitle}</td>
                    <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{page.pageViews}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg border border-border-subtle text-center text-text-gray min-h-[300px] flex items-center justify-center"> {/* Added min-h and flex for centering */}
          No most visited pages data available.
        </div>
      )}
    </div>
  );
};

export default Dashboard;